/**
 * Ce qu'un geste de programmation va produire, dit avant le clic.
 *
 * Aucune fonction ici ne décide d'un prix : elles préviennent. Le tarif
 * proratisé montré en aperçu est recalculé avec la MÊME formule que
 * `programme_tarif_semaine`, et c'est le seul endroit du navigateur où cette
 * règle est reproduite — voir `tarifProratise` pour pourquoi c'est acceptable
 * ici et pourquoi la base reste la seule autorité.
 */

/** Une semaine déclarée, telle que le formulaire en a besoin. */
export interface SemaineChoisie {
  id: string;
  libelle: string;
  debut: string;
  fin: string;
  jours_ouverts: number;
  jours_reference: number;
}

/** Ce que l'écran s'apprête à demander. */
export interface Saisie {
  stage_id: string;
  center_ids: string[];
  semaine_ids: string[];
  prix_normal: string;
  prix_reduit: string;
  prix_local: string;
  prix_local_reduit: string;
  tarif_condition_id: string;
  capacite: string;
  ouverture: string;
}

export function nombreOuNull(v: string | null | undefined): number | null {
  const t = (v ?? '').trim();
  if (t === '') return null;
  const n = Number(t.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/** Un montant en euros, tel que la base les porte (`numeric`, pas des centimes).
 *
 * `orders/devis.ts` a déjà un `euros()`, mais il prend des CENTIMES : c'est la
 * monnaie du circuit de paiement, où un entier interdit les erreurs de virgule
 * flottante. Les prix d'une session sont des `numeric` en euros ; les convertir
 * en centimes pour les reformater ajouterait un arrondi là où il n'y en a pas
 * besoin. Deux monnaies, deux formateurs.
 */
export function prix(montant: number | null | undefined): string {
  if (montant === null || montant === undefined || Number.isNaN(montant)) return '—';
  return `${montant.toFixed(2).replace('.', ',')} €`;
}

/**
 * Le message à montrer pour une erreur attrapée dans un `catch`.
 *
 * TypeScript type ce que `catch` attrape en `unknown`, et non en `Error` : une
 * promesse peut être rejetée avec n'importe quoi. Les écrans écrivaient donc
 * `catch (err: any)` puis `err?.message`, ce qui marche mais éteint le
 * vérificateur — et laisserait passer un `err.mesage` mal orthographié.
 *
 * On ne lit `.message` que sur un vrai `Error`. PostgREST rejette avec un objet
 * qui n'en est pas un mais qui porte quand même `message` : on l'accepte s'il
 * est une chaîne non vide, sinon on rend le repli plutôt qu'un « undefined »
 * affiché à l'écran.
 */
export function messageDErreur(err: unknown, repli: string): string {
  if (err instanceof Error && err.message.trim()) return err.message;
  if (typeof err === 'object' && err !== null && 'message' in err) {
    const m = (err as { message: unknown }).message;
    if (typeof m === 'string' && m.trim()) return m;
  }
  return repli;
}

/** « 4 jours sur 5 » quand la semaine est écourtée, sinon « 5 jours ». */
export function phraseDeJours(ouverts: number, reference: number): string {
  if (ouverts >= reference) return `${ouverts} jour${ouverts > 1 ? 's' : ''}`;
  return `${ouverts} jour${ouverts > 1 ? 's' : ''} sur ${reference}`;
}

/**
 * Le tarif d'une semaine écourtée.
 *
 * ## Pourquoi cette formule est reproduite ici
 *
 * `programme_tarif_semaine` en est l'autorité, et c'est elle qui écrit. Cette
 * copie ne sert QU'À l'aperçu : montrer « S2, 4 jours → 67,20 € » avant le clic.
 *
 * Deux copies d'une règle finissent par différer — c'est ce qui est arrivé au
 * tarif local, réimplémenté dans deux magasins dont aucun ne testait
 * `tarif_conditions.active`. Ce qui rend celle-ci tolérable, et il faut que ça
 * reste vrai : elle n'est jamais envoyée. Le formulaire transmet le tarif
 * PLEIN, la base proratise, et le compte rendu affiche ce qu'elle a écrit. Si
 * les deux divergeaient, l'écran le montrerait au retour — l'aperçu ne peut pas
 * fabriquer un prix faux, seulement une attente fausse.
 *
 * Et un test compare les deux sur les trois cas mesurés en production.
 */
export function tarifProratise(
  tarifPlein: number | null,
  joursOuverts: number,
  joursReference: number,
): number | null {
  if (tarifPlein === null || Number.isNaN(tarifPlein)) return null;
  if (joursReference <= 0 || joursOuverts <= 0) return tarifPlein;
  const ratio = Math.min(joursOuverts, joursReference) / joursReference;
  return Math.round(tarifPlein * ratio * 100) / 100;
}

export interface ApercuDeSemaine {
  semaine_id: string;
  libelle: string;
  jours: string;
  prixNormal: number | null;
  proratise: boolean;
}

/**
 * Le tarif que chaque semaine choisie portera.
 *
 * C'est la réponse au défaut le plus coûteux mesuré : la règle « semaine de
 * 4 jours = tarif × 4/5 » existait et n'était appliquée nulle part — 3 019 €
 * d'écart sur trois périodes. Un tarif qu'on découvre après coup est un tarif
 * qu'on n'a pas choisi.
 */
export function apercuParSemaine(
  semaines: SemaineChoisie[],
  tarifPlein: number | null,
): ApercuDeSemaine[] {
  return semaines.map((s) => {
    const p = tarifProratise(tarifPlein, s.jours_ouverts, s.jours_reference);
    return {
      semaine_id: s.id,
      libelle: s.libelle,
      jours: phraseDeJours(s.jours_ouverts, s.jours_reference),
      prixNormal: p,
      proratise: s.jours_ouverts < s.jours_reference && p !== tarifPlein,
    };
  });
}

export type Gravite = 'bloquant' | 'attention' | 'information';

export interface Avis {
  gravite: Gravite;
  genre: string;
  phrase: string;
}

/** Une session déjà programmée, pour savoir ce que le geste va écraser. */
export interface SessionExistante {
  session_id: string;
  semaine_id: string;
  center_id: string;
  stage_id: string;
  centre: string;
  semaine: string;
  capacite: number;
  inscrits: number;
  archived: boolean;
}

/**
 * Ce que le geste a à dire avant d'être fait.
 *
 * Les refus sont en base — c'est elle qui doit tenir quel que soit l'écran.
 * Cette liste les ANTICIPE, pour qu'un refus n'arrive pas après avoir rempli
 * huit champs, et ajoute ce qui n'est pas une faute mais mérite un regard.
 */
export function avis(
  saisie: Saisie,
  semaines: SemaineChoisie[],
  existantes: SessionExistante[],
): Avis[] {
  const liste: Avis[] = [];
  const normal = nombreOuNull(saisie.prix_normal);
  const reduit = nombreOuNull(saisie.prix_reduit);
  const local = nombreOuNull(saisie.prix_local);
  const localReduit = nombreOuNull(saisie.prix_local_reduit);
  const capacite = nombreOuNull(saisie.capacite);

  const nbCentres = saisie.center_ids.length;
  const nbSemaines = saisie.semaine_ids.length;

  /* --- Ce qui empêche --- */
  if (!saisie.stage_id) {
    liste.push({ gravite: 'bloquant', genre: 'stage', phrase: 'Choisissez un stage.' });
  }
  if (nbSemaines === 0) {
    liste.push({
      gravite: 'bloquant',
      genre: 'semaines',
      phrase: 'Choisissez au moins une semaine.',
    });
  }
  if (nbCentres === 0) {
    liste.push({
      gravite: 'bloquant',
      genre: 'centres',
      phrase: 'Choisissez au moins un centre.',
    });
  }

  if (normal === null) {
    liste.push({
      gravite: 'bloquant',
      genre: 'tarif-normal',
      phrase: 'Le tarif normal est obligatoire.',
    });
  } else {
    if (normal < 0) {
      liste.push({
        gravite: 'bloquant',
        genre: 'tarif-negatif',
        phrase: 'Le tarif normal ne peut pas être négatif.',
      });
    }
    /*
      Les trois inégalités d'un rabais. Zéro violation sur les 345 sessions de
      production, sur sept périodes : ce sont toujours des fautes de frappe, et
      elles se commettent à 22h sur la quarantième ligne.
    */
    if (reduit !== null && reduit > normal) {
      liste.push({
        gravite: 'bloquant',
        genre: 'reduit-au-dessus',
        phrase: `Le tarif réduit (${prix(reduit)}) est au-dessus du tarif normal (${prix(normal)}).`,
      });
    }
    if (local !== null && local > normal) {
      liste.push({
        gravite: 'bloquant',
        genre: 'local-au-dessus',
        phrase: `Le tarif local (${prix(local)}) est au-dessus du tarif normal (${prix(normal)}).`,
      });
    }
  }
  const base = local ?? normal;
  if (localReduit !== null && base !== null && localReduit > base) {
    liste.push({
      gravite: 'bloquant',
      genre: 'local-reduit-au-dessus',
      phrase: `Le tarif local réduit (${prix(localReduit)}) est au-dessus du tarif local (${prix(base)}).`,
    });
  }

  /* Les cellules que le geste va toucher. */
  const visees = existantes.filter(
    (e) =>
      e.stage_id === saisie.stage_id &&
      saisie.center_ids.includes(e.center_id) &&
      saisie.semaine_ids.includes(e.semaine_id),
  );

  if (capacite === null) {
    /*
      Sans capacité saisie, la base reprend celle que la session portait déjà.
      C'est légitime sur une reprogrammation ; impossible sur une création.
    */
    const aCreer = nbCentres * nbSemaines - visees.length;
    if (aCreer > 0) {
      liste.push({
        gravite: 'bloquant',
        genre: 'capacite-absente',
        phrase:
          `${aCreer} session${aCreer > 1 ? 's' : ''} à créer, et aucun nombre de places : ` +
          'précisez-le.',
      });
    }
  } else if (capacite < 1) {
    liste.push({
      gravite: 'bloquant',
      genre: 'capacite-invalide',
      phrase: 'Le nombre de places doit être au moins 1.',
    });
  } else {
    const tropPetites = visees.filter((e) => e.inscrits > capacite);
    if (tropPetites.length > 0) {
      const noms = tropPetites
        .slice(0, 3)
        .map((e) => `${e.semaine} · ${e.centre} (${e.inscrits} inscrits)`)
        .join(', ');
      liste.push({
        gravite: 'bloquant',
        genre: 'capacite-sous-inscrits',
        phrase:
          `${capacite} places, mais ${tropPetites.length} session${tropPetites.length > 1 ? 's ont' : ' a'} ` +
          `déjà plus d’inscrits : ${noms}${tropPetites.length > 3 ? '…' : ''}.`,
      });
    }
  }

  /* --- Ce qui mérite un regard --- */
  /*
    Un tarif local sans condition ne s'applique à personne :
    `resolve_registration_price` ne l'accorde que si la session porte une
    condition. 46 sessions actives étaient dans cet état en septembre 2026.
  */
  if (local !== null && !saisie.tarif_condition_id) {
    liste.push({
      gravite: 'attention',
      genre: 'local-sans-condition',
      phrase:
        'Un tarif local est renseigné sans condition tarifaire : il ne s’appliquera à ' +
        'personne. Choisissez la condition, ou videz le tarif local.',
    });
  }
  if (local === null && saisie.tarif_condition_id) {
    liste.push({
      gravite: 'attention',
      genre: 'condition-sans-local',
      phrase:
        'Une condition tarifaire est choisie sans tarif local : les familles éligibles ' +
        'paieront le tarif normal.',
    });
  }

  const archivees = visees.filter((e) => e.archived);
  if (archivees.length > 0) {
    liste.push({
      gravite: 'attention',
      genre: 'session-archivee',
      phrase:
        `${archivees.length} session${archivees.length > 1 ? 's' : ''} visée${archivees.length > 1 ? 's sont' : ' est'} ` +
        'archivée : elle sera mise à jour, mais laissée archivée.',
    });
  }

  /* --- Ce qui va se passer --- */
  const semainesChoisies = semaines.filter((s) => saisie.semaine_ids.includes(s.id));
  const ecourtees = semainesChoisies.filter((s) => s.jours_ouverts < s.jours_reference);
  if (ecourtees.length > 0 && normal !== null) {
    liste.push({
      gravite: 'information',
      genre: 'tarif-proratise',
      phrase:
        `${ecourtees.length} semaine${ecourtees.length > 1 ? 's' : ''} écourtée${ecourtees.length > 1 ? 's' : ''} ` +
        `(${ecourtees.map((s) => s.libelle).join(', ')}) : le tarif normal y sera proratisé. ` +
        'Le tarif réduit et le tarif local ne changent pas.',
    });
  }

  const rang: Record<Gravite, number> = { bloquant: 0, attention: 1, information: 2 };
  return liste.sort((a, b) => rang[a.gravite] - rang[b.gravite]);
}

/** Vrai si quelque chose empêche de programmer. */
export function bloque(liste: Avis[]): boolean {
  return liste.some((a) => a.gravite === 'bloquant');
}

/**
 * Combien de sessions le geste va produire, et lesquelles existent déjà.
 *
 * « 8 sessions : 6 à créer, 2 à mettre à jour » vaut mieux que « appliquer » :
 * la différence compte quand on croyait ne faire que l'un.
 */
export function portee(
  saisie: Saisie,
  existantes: SessionExistante[],
): { total: number; aCreer: number; aMettreAJour: number } {
  const total = saisie.center_ids.length * saisie.semaine_ids.length;
  const aMettreAJour = existantes.filter(
    (e) =>
      e.stage_id === saisie.stage_id &&
      saisie.center_ids.includes(e.center_id) &&
      saisie.semaine_ids.includes(e.semaine_id),
  ).length;
  return { total, aCreer: total - aMettreAJour, aMettreAJour };
}

/** Résume ce qu'un geste a fait. */
export function resumeDuGeste(
  resultats: { action: 'creee' | 'mise_a_jour' }[],
): string {
  const creees = resultats.filter((r) => r.action === 'creee').length;
  const majs = resultats.filter((r) => r.action === 'mise_a_jour').length;

  const bouts: string[] = [];
  if (creees > 0) bouts.push(`${creees} session${creees > 1 ? 's créées' : ' créée'}`);
  if (majs > 0) bouts.push(`${majs} mise${majs > 1 ? 's' : ''} à jour`);
  if (bouts.length === 0) return 'Rien à programmer.';
  return `${bouts.join(', ')}.`;
}
